import React, { useState } from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import api from '../utils/api';
import './AppointmentForm.css';

const WHATSAPP_URL = 'https://wa.me/919140395816?text=Hi%2C%20I%27d%20like%20to%20book%20a%20first%20session%20at%20Mind%20Spa.';
const EMPTY = { firstName: '', lastName: '', email: '', phone: '', message: '' };

const AppointmentForm = () => {
  const [formData, setFormData] = useState(EMPTY);
  const [status, setStatus] = useState('idle');   // 'idle' | 'sending' | 'ok' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const [okMsg, setOkMsg] = useState('');
  const [revealRef, revealVis] = useScrollReveal();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMsg('');
    try {
      const res = await api.submitAppointment(formData);
      setOkMsg(res?.message || "Thanks — we'll be in touch shortly.");
      setStatus('ok');
      setFormData(EMPTY);
    } catch (err) {
      setErrorMsg(err.message || 'Could not send your request. Please try WhatsApp instead.');
      setStatus('error');
    }
  };

  return (
    <section className="appointment">
      <div className="container">
        <div ref={revealRef} className={`appointment__card ${revealVis ? 'visible' : ''}`}>
          {/* LEFT — image + warm panel */}
          <div className="appointment__media">
            <img
              src="/assets/books/book-appointment.webp"
              alt="The Mind Spa consultation room in Lucknow"
              className="appointment__image"
              loading="lazy"
            />
            <div className="appointment__media-overlay">
              <p className="appointment__media-eyebrow">Start your journey</p>
              <h3 className="appointment__media-title">
                Book your personalised session.
              </h3>
              <p className="appointment__media-text">
                Fill the form and we&apos;ll get back to you or message us
                directly for a faster response.
              </p>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="appointment__whatsapp"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                <span>WhatsApp instead</span>
              </a>
            </div>
          </div>

          {/* RIGHT — form */}
          <div className="appointment__form-wrap">
            <p className="section-label" style={{ textAlign: 'left' }}>Book a session</p>
            <h2 className="appointment__form-title">Tell us a little about yourself.</h2>

            {status === 'ok' && (
              <div className="appointment__success" role="status">
                {okMsg}
              </div>
            )}
            {status === 'error' && (
              <div className="appointment__success" role="alert"
                   style={{ background: 'var(--accent-warm-light)', borderColor: 'var(--accent-warm)', color: '#6a4a1e' }}>
                {errorMsg}
              </div>
            )}

            <form className="appointment__form" onSubmit={handleSubmit} noValidate>
              <div className="appointment__row">
                <label className="appointment__field">
                  <span>First name</span>
                  <input
                    type="text"
                    name="firstName"
                    placeholder="Your first name"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    autoComplete="given-name"
                  />
                </label>
                <label className="appointment__field">
                  <span>Last name</span>
                  <input
                    type="text"
                    name="lastName"
                    placeholder="Your last name"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    autoComplete="family-name"
                  />
                </label>
              </div>
              <div className="appointment__row">
                <label className="appointment__field">
                  <span>Email</span>
                  <input
                    type="email"
                    name="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    autoComplete="email"
                  />
                </label>
                <label className="appointment__field">
                  <span>Phone</span>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="+91 …"
                    value={formData.phone}
                    onChange={handleChange}
                    autoComplete="tel"
                  />
                </label>
              </div>
              <label className="appointment__field">
                <span>What would you like to talk about?</span>
                <textarea
                  name="message"
                  placeholder="Briefly tell us what's going on — anxiety, stress, sleep, a recent change… anything is fine."
                  rows="5"
                  value={formData.message}
                  onChange={handleChange}
                  required
                />
              </label>

              <div className="appointment__footer">
                <button
                  type="submit"
                  className="btn-primary appointment__submit"
                  disabled={status === 'sending'}
                >
                  {status === 'sending' ? 'Sending…' : 'Request an Appointment'}
                </button>
                <p className="appointment__legal">
                  We&apos;ll only use your details to contact you about your enquiry.
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AppointmentForm;
